/**
 * Admin/AdminPanel.tsx
 *
 * Container for the Admin tab.  Renders a simple segmented nav that switches
 * between the four admin sub-views using adminView from the Zustand store so
 * that the active view persists across tab switches.
 *
 * Sub-views:
 *   status   → InstallerStatus   — worker health + what was installed
 *   reset    → ResetControls     — soft / hard reset
 *   columns  → ColumnMappingEditor — view/edit monday column IDs
 *   presets  → PresetManager     — save/load named board sets
 */

import { useAppStore } from '@core/store/appStore';
import type { AdminView } from '@core/types';
import { InstallerStatus } from './InstallerStatus';
import { ResetControls } from './ResetControls';
import { ColumnMappingEditor } from './ColumnMappingEditor';
import { PresetManager } from './PresetManager';
import { BoardConnector } from './BoardConnector';

// ─── Nav config ───────────────────────────────────────────────────────────────

interface NavItem {
  id: AdminView;
  label: string;
  icon: string;
  description: string;
}

const NAV_ITEMS: NavItem[] = [
  {
    id: 'status',
    label: 'Status',
    icon: '🟢',
    description: 'Worker health and installer status',
  },
  {
    id: 'boards',
    label: 'Boards',
    icon: '🔗',
    description: 'Connect workspace boards to Chanelle OS',
  },
  {
    id: 'reset',
    label: 'Reset',
    icon: '🔄',
    description: 'Clear storage or re-run the installer',
  },
  {
    id: 'columns',
    label: 'Columns',
    icon: '🗂️',
    description: 'Review and correct column ID mappings',
  },
  {
    id: 'presets',
    label: 'Presets',
    icon: '📦',
    description: 'Save and switch named board groups',
  },
];

// ─── Component ────────────────────────────────────────────────────────────────

export function AdminPanel() {
  const { adminView, setAdminView } = useAppStore((s) => ({
    adminView: s.adminView,
    setAdminView: s.setAdminView,
  }));

  return (
    <div className="cos-admin-panel">
      {/* ── Header ──────────────────────────────────────────────────────────── */}
      <header className="cos-admin-panel__header">
        <h2 className="cos-admin-panel__title">
          <span aria-hidden="true">🔧</span> Admin
        </h2>
        <p className="cos-admin-panel__subtitle">
          Installation status, storage controls, and configuration tools.
        </p>
      </header>

      {/* ── Segmented nav ───────────────────────────────────────────────────── */}
      <nav className="cos-admin-nav" role="tablist" aria-label="Admin sections">
        {NAV_ITEMS.map((item) => (
          <button
            key={item.id}
            type="button"
            role="tab"
            aria-selected={adminView === item.id}
            aria-controls={`admin-panel-${item.id}`}
            className={`cos-admin-nav__item${adminView === item.id ? ' cos-admin-nav__item--active' : ''}`}
            onClick={() => setAdminView(item.id)}
            title={item.description}
          >
            <span className="cos-admin-nav__icon" aria-hidden="true">
              {item.icon}
            </span>
            <span className="cos-admin-nav__label">{item.label}</span>
          </button>
        ))}
      </nav>

      {/* ── Active panel ────────────────────────────────────────────────────── */}
      <main className="cos-admin-panel__body">
        {adminView === 'status' && (
          <div
            id="admin-panel-status"
            role="tabpanel"
            aria-label="Installer status"
          >
            <InstallerStatus />
          </div>
        )}

        {adminView === 'boards' && (
          <div
            id="admin-panel-boards"
            role="tabpanel"
            aria-label="Board connector"
          >
            <BoardConnector />
          </div>
        )}

        {adminView === 'reset' && (
          <div
            id="admin-panel-reset"
            role="tabpanel"
            aria-label="Reset controls"
          >
            <ResetControls />
          </div>
        )}

        {adminView === 'columns' && (
          <div
            id="admin-panel-columns"
            role="tabpanel"
            aria-label="Column mapping editor"
          >
            <ColumnMappingEditor />
          </div>
        )}

        {adminView === 'presets' && (
          <div
            id="admin-panel-presets"
            role="tabpanel"
            aria-label="Preset manager"
          >
            <PresetManager />
          </div>
        )}
      </main>
    </div>
  );
}
